// Import required dependencies
import { useRef, useEffect } from "react";
import Button from "components/button";
import { getPoweredByText } from "features/components/helperFunctions";
import { ThermalReceiptPrintProps } from "../index.types";

// exporting function to be used in other components
export let handlePrint: () => void;

const ThermalReceiptPrint = ({ orderData }: ThermalReceiptPrintProps) => {
  // Reference to hidden iframe used for printing
  const printFrameRef = useRef<HTMLIFrameElement | null>(null);

  // Get business information from local storage
  const { eatout_name } = JSON.parse(localStorage.getItem("businessInfo")!);
  const { businessPhoneNo } = JSON.parse(localStorage.getItem("ts-global")!);

  // Configuration constants for receipt layout
  const paperWidth = 80;
  const fontSize = 8;
  const charWidth = 42;

  // ==============================|| useEffect hook ||============================== //

  // Create and manage hidden iframe for printing
  useEffect(() => {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.id = "print-frame";
    document.body.appendChild(iframe);

    printFrameRef.current = iframe;

    return () => {
      document.body.removeChild(iframe);
    };
  }, []);

  // ==============================|| handler functions ||============================== //

  // Main function to handle receipt printing
  handlePrint = (): void => {
    if (!printFrameRef.current || !printFrameRef.current.contentWindow) {
      return;
    }

    // Get reference to print window
    const printWindow = printFrameRef.current.contentWindow;
    printWindow.document.open();

    // Generate receipt content
    const headerContent: string = generateHeaderContent();
    const itemsContent: string = generateItemsContent();
    const totalsContent: string = generateTotalsContent();
    const footerContent: string = generateFooterContent();

    // Create a print document with structured content
    printWindow.document.write(`
  <!DOCTYPE html>
  <html>
    <head>
      <title>Print Receipt</title>
      <style>
        @page {
          size: ${paperWidth}mm auto;
          margin: 0;
        }
        html, body {
          margin: 0;
          padding: 0;
          width: 100%;
          height: auto;
          display: flex;
          justify-content: center;
          align-items: flex-start;
        }
        .receipt-container {
          width: ${paperWidth}mm;
          font-family: 'Courier New', monospace;
          font-size: ${fontSize}pt;
          line-height: 1.1;
          margin: 0 auto;
          text-align: left;
        }
        .receipt-section {
          white-space: pre-wrap;
          width: 100%;
        }
        .footer {
          margin-top: 10px;
          text-align: center;
        }
      </style>
    </head>
    <body>
      <div class="receipt-container">
        <div class="receipt-section header">${headerContent}</div>
        <div class="receipt-section items">${itemsContent}</div>
        <div class="receipt-section totals">${totalsContent}</div>
        <div class="receipt-section footer">${footerContent}</div>
      </div>
      <script>
        window.onload = function() {
          setTimeout(function() {
            window.print();
            window.close();
          }, 100);
        };
      </script>
    </body>
  </html>
`);

    printWindow.document.close();
  };

  // Utility function to center text within the receipt width
  const centerText = (text: string): string => {
    const padding: number = Math.max(0, charWidth - text.length) / 2;
    return " ".repeat(Math.floor(padding)) + text;
  };

  // Creates a horizontal line using specified character
  const line = (char = "-"): string => char.repeat(charWidth);

  // Generates multiple newlines for spacing
  const generateNewLines = (repetitions: number): string => {
    return " \n".repeat(repetitions);
  };

  // Adds emphasis to text by surrounding it with asterisks
  const emphasize = (text: string): string => {
    return `*** ${text} ***`;
  };

  // Generates the business info and order details header
  const generateHeaderContent = (): string => {
    let content: string = "";

    // Business info
    const storeName = eatout_name;
    const storeAddress: string = orderData.eatout_address ?? "";
    const storeLocation: string = orderData.eatout_location
      ? `, ${orderData.eatout_location}`
      : "";

    content += centerText(storeName) + "\n";
    content += generateNewLines(1);
    content += centerText(`${storeAddress}${storeLocation}`) + "\n";
    content +=
      centerText(`Tel: ${JSON.parse(businessPhoneNo) || businessPhoneNo}`) +
      "\n";

    content += generateNewLines(1);

    // Receipt header
    content += line("=") + "\n";
    content += generateNewLines(1);
    content += centerText(emphasize("USER RECEIPT")) + "\n";
    content += generateNewLines(1);

    // Order info
    const orderId: string = orderData.order_id ?? "0";
    content += generateNewLines(1);
    content += centerText(emphasize(`Order # ${orderId}`)) + "\n";
    content += generateNewLines(1);

    if (orderData.order_type) {
      content +=
        centerText(emphasize(`${orderData.order_type.toUpperCase()} - ORDER`)) +
        "\n";
    }

    content += generateNewLines(1);
    content += line("=") + "\n";
    content += generateNewLines(1);

    // Customer info
    const customerName: string = orderData.name ?? "Walk in";
    const customerPhone: string = orderData.mobile_number?.trim() || "";

    content += formatLine("Customer", customerName) + "\n";
    if (customerPhone) {
      content += formatLine("Phone", customerPhone) + "\n";
    }

    if (orderData.address) {
      content += "Customer Address\n";
      content += `${orderData.address}\n`;
    }

    content += generateNewLines(1);

    return content;
  };

  // Generates the itemized list of products
  const generateItemsContent = (): string => {
    let content: string = "";

    content += line("-") + "\n";
    content += formatItemRow("QTY", "NAME", "TOTAL") + "\n";
    content += line("-") + "\n";
    content += generateNewLines(1);

    if (
      Array.isArray(orderData.order_detail) &&
      orderData.order_detail.length
    ) {
      orderData.order_detail.forEach((item) => {
        if (!item) return;

        const itemName: string = item.item_name;
        const quantity: string = item.quantity ?? "1";
        const price: string = item?.total?.toString() ?? "0";
        const formattedPrice: string = formatCurrency(price);

        const qtyWidth: number = 3;
        const totalWidth: number = 6;
        const spacesCount: number = 2;
        const nameWidth: number =
          charWidth - qtyWidth - totalWidth - spacesCount;

        if (itemName.length <= nameWidth) {
          content += formatItemRow(quantity, itemName, formattedPrice) + "\n";
        } else {
          const firstLineName: string =
            itemName.substring(0, nameWidth - 1) + "-";
          content +=
            formatItemRow(quantity, firstLineName, formattedPrice) + "\n";
          const remainingName: string = itemName.substring(nameWidth - 1);
          content += " ".repeat(qtyWidth + 1) + remainingName + "\n";
        }

        try {
          if (
            item.options &&
            item.options !== "{}" &&
            item.options !== "null"
          ) {
            const options = JSON.parse(item.options);
            content = processItemOptionsWithFormat(options, content);
          }
        } catch (e) {
          console.error("Error parsing options:", e);
        }
      });
    } else {
      content += "No items in order\n";
    }

    return content;
  };

  // Generates the totals section with calculations
  const generateTotalsContent = (): string => {
    let content: string = "";

    content += line("-") + "\n";

    const subtotal: string = orderData.total ?? "0";
    content += formatLine("Subtotal", formatCurrency(subtotal)) + "\n";

    const discountValue: string = orderData.discount_value ?? "0";
    if (Number.parseFloat(discountValue) > 0) {
      content += formatLine("Discount", formatCurrency(discountValue)) + "\n";
    }

    const taxValue: string = orderData.tax_value ?? "0";
    if (Number.parseFloat(taxValue) > 0) {
      content += formatLine("Tax", formatCurrency(taxValue)) + "\n";
    }

    const deliveryCharges: string = orderData.delivery_charges ?? "0";
    if (Number.parseFloat(deliveryCharges) > 0) {
      content +=
        formatLine("Delivery Charges", formatCurrency(deliveryCharges)) + "\n";
    }

    content +=
      formatLine("Total", formatCurrency(orderData.grand_total)) + "\n";

    content += line("-") + "\n";
    content += generateNewLines(1);

    if (orderData.payment_type) {
      content += centerText(`Payment Method: ${orderData.payment_type}`) + "\n";
    }

    content += generateNewLines(2);

    return content;
  };

  // Generates the footer with thank you message
  const generateFooterContent = (): string => {
    let content: string = "\n";
    content += centerText("Thank you for your purchase!") + "\n";
    content += centerText(getPoweredByText()) + "\n";
    content += generateNewLines(35);
    return content;
  };

  // Formats a line with left and right aligned text
  const formatLine = (left: string, right: string): string => {
    const minPriceSpace: number = 10;
    const maxLeftWidth: number = charWidth - minPriceSpace - 1;

    const truncatedLeft: string =
      left.length > maxLeftWidth
        ? left.substring(0, maxLeftWidth - 3) + "..."
        : left;

    const spaces: number = charWidth - truncatedLeft.length - right.length;

    if (spaces < 1) {
      const truncatedRight =
        right.length > minPriceSpace
          ? right.substring(0, minPriceSpace)
          : right;
      return truncatedLeft + " " + truncatedRight;
    }

    return truncatedLeft + " ".repeat(Math.max(1, spaces)) + right;
  };

  // Formats an item row with quantity, name and total
  const formatItemRow = (
    quantity: string,
    name: string,
    total: string
  ): string => {
    const qtyWidth: number = 3;
    const totalWidth: number = 6;
    const spacesCount: number = 2;
    const nameWidth: number = charWidth - qtyWidth - totalWidth - spacesCount;

    let qtyStr: string = String(quantity) + "x";
    if (qtyStr.length > qtyWidth) {
      qtyStr = qtyStr.substring(0, qtyWidth);
    } else {
      qtyStr = qtyStr.padEnd(qtyWidth, " ");
    }

    let totalStr: string = total.substring(0, totalWidth);
    totalStr = totalStr.padStart(totalWidth, " ");

    let nameStr: string = name;
    if (nameStr.length > nameWidth) {
      nameStr = nameStr.substring(0, nameWidth);
    } else {
      nameStr = nameStr.padEnd(nameWidth, " ");
    }

    return qtyStr + " " + nameStr + " " + totalStr;
  };

  // Formats an option row with indentation
  const formatOptionRow = (quantity: any, name: any, total: string): string => {
    const qtyWidth: number = 3;
    const totalWidth: number = 6;
    const spacesCount: number = 2;
    const indentWidth: number = 4;
    const prefixWidth: number = 4;
    const nameWidth: number =
      charWidth -
      qtyWidth -
      totalWidth -
      spacesCount -
      indentWidth -
      prefixWidth;

    let qtyStr: string = String(quantity) + "x";
    if (qtyStr.length > qtyWidth) {
      qtyStr = qtyStr.substring(0, qtyWidth);
    } else {
      qtyStr = qtyStr.padEnd(qtyWidth, " ");
    }

    let totalStr: string = total.substring(0, totalWidth);
    totalStr = totalStr.padStart(totalWidth, " ");

    let nameStr = name;
    if (nameStr.length > nameWidth) {
      nameStr = nameStr.substring(0, nameWidth);
    } else {
      nameStr = nameStr.padEnd(nameWidth, " ");
    }

    return (
      " ".repeat(indentWidth) +
      "==>" +
      " " +
      qtyStr +
      " " +
      nameStr +
      " " +
      totalStr
    );
  };

  // Processes item options recursively
  const processItemOptionsWithFormat = (
    options: any[] | null,
    receipt: string
  ) => {
    let result: string = receipt;

    if (Array.isArray(options)) {
      options.forEach((opt) => {
        if (!opt) return;
        const optPrice: string = opt.price ? formatCurrency(opt.price) : "";
        const optQuantity = opt.quantity ?? "1";
        const optName = opt.name ?? "Option";
        result += formatOptionRow(optQuantity, optName, optPrice) + "\n";
      });
    } else if (typeof options === "object" && options !== null) {
      Object.keys(options).forEach((key) => {
        const option = options[key];
        if (Array.isArray(option)) {
          (option as any[]).forEach(
            (opt: {
              price: string;
              quantity: null | undefined;
              name: null | undefined;
            }) => {
              if (!opt) return;
              const optPrice = opt.price ? formatCurrency(opt.price) : "";
              const optQuantity = opt.quantity ?? "1";
              const optName = opt.name ?? "Option";
              result += formatOptionRow(optQuantity, optName, optPrice) + "\n";
            }
          );
        } else if (option) {
          result += `    ==> ${key}: ${options[key]}\n`;
        }
      });
    }

    return result;
  };

  // Formats currency values consistently
  const formatCurrency = (amount: string): string => {
    if (!amount) return "0";
    try {
      return Number.parseInt(amount).toLocaleString();
    } catch (e) {
      return amount;
    }
  };

  return <></>;
};

export default ThermalReceiptPrint;
